import React, { createRef, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Chip } from '@mui/material'
import { heightCategory, marginX } from '~/constants'
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { menuTemplate } from '~/constants/menu'
import { useTranslation } from 'react-i18next'

const TopCategories = ({ categories, onClickItemCategory, idActiveCategory, top }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [isScrollFirstTime, setIsScrollFirstTime] = useState(false) // đánh dấu chỉ cuộn một lần trong trường hợp truyền category_ID trên url từ các màn khác vào màn này
  const category_ID = searchParams.get('category_ID')
  const [idActive, setIdActive] = useState(categories[0]?.id)
  const boxCategoryRef = useRef()
  const menuCategoryRef = useRef([])
  const { t } = useTranslation()

  /**
   * Tạo ra mảng menuCategoryRef được đánh index theo độ dài của categories đã xác định
   * Phục vụ cho việc lấy ra thông số offsetLeft của mỗi item category
   */
  menuCategoryRef.current = useMemo(() => {
    return [...Array(categories.length)].map((_, i) => menuCategoryRef.current[i] ?? createRef())
  }, [categories.length])

  useEffect(() => {
    setIdActive(categories[0]?.id)
  }, [categories.length])

  /**
   * Lắng nghe khi người dùng cuộn tới category nào thì active nó
   */
  useEffect(() => {
    if (idActiveCategory) setIdActive(idActiveCategory)
  }, [idActiveCategory])

  const handleScrollLeft = useCallback((offsetLeft) => {
    boxCategoryRef.current.scrollTo({
      top: 0,
      left: offsetLeft,
      behavior: 'smooth',
    })
  }, [])

  /**
   * Khi một item được active => tìm kiếm item đó và lấy ra ref của item
   * Từ ref của item => lấy được offsetLeft
   * Cuối cùng xử lý cuộn sang trái
   */
  useEffect(() => {
    const indexItemCategory = categories.findIndex((item) => item.id === idActiveCategory)
    if (indexItemCategory >= 0) {
      const itemMenuCategoryRef = menuCategoryRef?.current?.[indexItemCategory]

      const offsetLeft = itemMenuCategoryRef?.current?.offsetLeft - marginX

      handleScrollLeft(offsetLeft)
    }
  }, [idActiveCategory, categories.length])

  const clickItemCategoryHandler = useCallback(
    (id, index) => {
      setIdActive(Number(id))
      onClickItemCategory(index)
    },
    [onClickItemCategory],
  )

  // Khi  category cuộn đến chỗ category nào thì query params lên url đó
  useEffect(() => {
    if (idActive) {
      const params = {
        category_ID: idActive,
      }
      setSearchParams(params)
    }
  }, [idActive, setSearchParams])

  /**
   * Lắng nghe khi người dùng đi từ màn hình home vào màn này và truyền theo category_ID trên params
   * Xử lý cuộn xuống category đó và cuộn trái
   * Chỉ cuộn lần đầu tiên
   */
  useEffect(() => {
    if (category_ID && !isScrollFirstTime) {
      const indexItemCategory = categories.findIndex((item) => item.id === Number(category_ID))
      if (indexItemCategory >= 0) {
        setIsScrollFirstTime(true)
        clickItemCategoryHandler(category_ID, indexItemCategory)
      }
    }
  }, [category_ID, categories.length, isScrollFirstTime])

  const renderCategories = () => {
    return categories.map((item, index) => {
      const { id, name, category_template } = item
      let nameUpdate = name
      if (category_template === menuTemplate.videoDish) nameUpdate = t('titles.popular')

      return (
        <Box
          key={id}
          onClick={() => clickItemCategoryHandler(id, index)}
          ref={menuCategoryRef?.current[index]}>
          <Chip label={nameUpdate} variant={idActive === id ? 'active' : 'unactive'} />
        </Box>
      )
    })
  }

  if (!Array.isArray(categories)) return null

  return (
    <Box
      top={top}
      component="div"
      style={styles.container}
      bgcolor="baseColor.color1"
      ref={boxCategoryRef}>
      {renderCategories()}
    </Box>
  )
}

TopCategories.propTypes = {
  categories: PropTypes.array,
  onClickItemCategory: PropTypes.func,
  idActiveCategory: PropTypes.any,
  top: PropTypes.number,
}

export default React.memo(TopCategories)

const styles = {
  container: {
    position: 'fixed',
    padding: '14px 20px',
    overflowY: 'scroll',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    zIndex: 1,
    maxHeight: heightCategory,
    width: '100%',
    transition: '0.3s',
  },
}
